import React, { useMemo } from 'react';
import styled from 'styled-components';

import DatePicker from 'antd/es/date-picker';

import featureToggles from 'config/featureToggles';

import useProcessStats, { ProcessDirection } from 'hooks/useProcessStats';
import useQueryParam from 'hooks/useQueryParam';
import usePersistentDatePicker from 'hooks/usePersistentDatePicker';
import useSearcher from 'hooks/useSearcher';
import { useDoorSelect, useLocationSelect } from 'hooks/useSelect';

import MainLayout from 'layouts/Main.layout';
import { Row, Col, Space } from 'components/layout';
import PageHeader from 'components/MainPageHeader';
import Grid from 'components/Grid';
import Table from 'components/Table';
import StatsCard from 'components/StatsCard';
import FilterBar from 'components/FilterBar';
import Search from 'components/Search';
import Select from 'components/Select';
import PeriodFilter from 'components/PeriodFilter';
import { useFeatureToggleItem } from 'components/FeatureToggle';

import { datePickerFormats } from 'config/common';

const UnloadStatsPageHeader = styled(PageHeader)`
  & .ant-page-header-heading-sub-title {
    display: inline-flex;
    justify-content: center;
    flex: 1;
    margin-right: 100px;
  }
`;

const UnloadStats = () => {
  const individualsFeatureToggle = useFeatureToggleItem(featureToggles.FEATURE_INDIVIDUALS);
  const showIndividuals = individualsFeatureToggle?.visible ?? true;

  const datePicker = usePersistentDatePicker({ queryParam: 'dt' });
  const [searchVal, setSearchVal] = useQueryParam('s', '');


  const locationSelect = useLocationSelect();
  const doorSelect = useDoorSelect({ hasAllOption: true, locationId: locationSelect.value });
  const searcher = useSearcher([
    'door_id',
    'name',
  ]);

  const {
    isLoading,
    statsSummary,
    stats,
  } = useProcessStats(ProcessDirection.UNLOAD, locationSelect.value, {
    start: datePicker.date,
    end: datePicker.date
      ? datePicker.date.clone().endOf(datePicker.mode === 'date' ? 'day' : datePicker.mode)
      : null,
  });

  const statsResults = useMemo(() => {
    let { stats: statsResults } = stats.state.stats;
    if (statsResults && statsResults.length) {
      if (doorSelect.value && doorSelect.value !== 'all') {
        statsResults = statsResults.filter(door => {
          return door.door_id === doorSelect.value;
        });
      }
      searcher.setData(statsResults);
      statsResults = searcher.search(searchVal);
    }
    return statsResults;
  }, [searcher, stats.state.stats, searchVal, doorSelect.value]);

  return (
    <MainLayout>
      <UnloadStatsPageHeader
        title="Inbound"
        subTitle={
          <PeriodFilter selectedPeriod={datePicker.period} onChange={datePicker.setPeriod} />
        }
        icon="package"
      />

      <Row style={{ padding: '12px 24px' }}>
        <Space size="large">
          <Select
            options={doorSelect.options}
            value={doorSelect.value}
            onChange={doorSelect.onChange}
            style={{ width: '260px' }}
            placeholder="Select Door"
          />
          <DatePicker
            mode={datePicker.mode}
            picker={datePicker.mode}
            value={datePicker.date}
            format={datePickerFormats[datePicker.period]}
            onChange={date => datePicker.setDate(date)}
            style={{ width: '260px' }}
            allowClear={false}
          />
        </Space>
      </Row>

      <Grid gutter={24} columns={4} style={{ padding: '8px 24px 16px' }}>
      <StatsCard
          title="Equipment Unloaded"
          value={statsSummary.totContainers}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Average Time / Unload"
          value={statsSummary.avgContainerTime}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Cartons Unloaded"
          value={statsSummary.boxesUnloaded}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Average CPH"
          value={statsSummary.avgCph}
          titleAlign={['center', 'bottom']}
        />
      </Grid>

      <Row style={{ padding: '24px' }}>
        <Col span={24}>
          <FilterBar
            title="Doors"
            titleStyles={{ color: '#232526', fontSize: '30px', lineHeight: '38px' }}
          >
            <FilterBar.Slot align="right">
              <Search placeholder="Search Door Number" value={searchVal} onChange={setSearchVal} />
            </FilterBar.Slot>
          </FilterBar>

          <Table
            dataSource={statsResults}
            loading={isLoading}
            rowKey="door_id"
          >
            <Table.Column type="link" title="Door" dataIndex="name" to={({ item }) => `/doors/${item.door_id}`} />
            {/* <Table.Column title="Expended labor hour/container" dataIndex="avg_man_minutes" align="right" render={avgTime => {
              if (avgTime === undefined || avgTime === null) return '-';
              // in this case avg time is in mins
              return formatDuration(avgTime * 60 * 1000, false);
            }} /> */}
            <Table.Column title="Equipment Unloaded" dataIndex="tot_containers" align="right" />
            <Table.Column type="duration" title="Average Time / Unload" dataIndex="avg_time_per_container" withSecs={false} align="right" />
            <Table.Column type="number" title="Cartons Unloaded" dataIndex="tot_boxes" align="right" rounded={true} readable={true} />
            <Table.Column type="number" title="Average CPH" dataIndex="avg_cph" precision={2} readable={true} align="right" />
            {showIndividuals && (
              <Table.Column type="number" title="Average Individuals" dataIndex="avg_employees" precision={2} align="right" />
            )}
          </Table>
        </Col>
      </Row>

    </MainLayout>
  );
};

export default UnloadStats;
